import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { isTaskDone } from "../lib/taskStage";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "../lib/utils";
import TaskStageBadge from "../components/TaskStageBadge";
import {
  Search,
  Clock,
  CheckCircle,
  Eye,
  AlertTriangle,
  Filter,
  ChevronRight,
} from "lucide-react";

export default function PartnerTasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const projectsRes = await api.get("/trp/projects");
      setProjects(projectsRes.data);
      
      const allTasks = [];
      for (const project of projectsRes.data) {
        try {
          const tasksRes = await api.get(`/tasks?project_id=${project.id}`);
          const tasksWithProject = tasksRes.data.map(t => ({
            ...t,
            project_name: project.name,
          }));
          allTasks.push(...tasksWithProject);
        } catch (e) {
          // Skip projects with no tasks
        }
      }
      
      setTasks(allTasks);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const taskRequiresAction = (task) => {
    const hasVersionsAwaitingApproval = task.versions?.some(v => 
      v.internal_status === "approved" && !v.approved && !v.client_approved
    );
    return hasVersionsAwaitingApproval || task.status === "review";
  };

  const tasksRequiringAction = useMemo(() => tasks.filter(taskRequiresAction), [tasks]);

  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.name?.toLowerCase().includes(query) ||
        t.project_name?.toLowerCase().includes(query)
      );
    }
    if (filterStatus !== "all") {
      result = result.filter(t => (filterStatus === "done") === isTaskDone(t));
    }
    return result;
  }, [tasks, searchQuery, filterStatus]);

  const getTypeLabel = (type) => {
    const labels = {
      exterior_render: "Ext", interior_render: "Int", aerial_view: "Aerial",
      close_up: "Close", eye_level: "Eye", animation: "Anim",
      floor_plan: "Plan", vr: "VR", vexos: "VEXOS",
      exterior: "Ext", interior: "Int", aerial: "Aerial", other: "Other"
    };
    return labels[type] || type?.substring(0, 4) || "—";
  };

  const formatPrice = (p) => p ? `$${p.toLocaleString()}` : "—";
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { day: "2-digit", month: "short" }) : "—";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="partner-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-50">Tasks</h1>
          <p className="text-zinc-500 text-xs">{tasks.length} tasks • {projects.length} projects</p>
        </div>
      </div>

      {/* Action Required Alert */}
      {tasksRequiringAction.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-red-400 text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {tasksRequiringAction.length} task{tasksRequiringAction.length > 1 ? 's' : ''} require your action
          </p>
        </div>
      )}

      {/* Stats Row */}
      <div className="flex gap-2 text-xs">
        {[
          { key: "all", label: "All", count: tasks.length, color: "text-zinc-400" },
          { key: "active", label: "Active", count: tasks.filter(t => !isTaskDone(t)).length, color: "text-orange-400" },
          { key: "done", label: "Done", count: tasks.filter(t => isTaskDone(t)).length, color: "text-green-400" },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilterStatus(s.key)}
            className={cn(
              "px-3 py-1.5 rounded-md transition-colors",
              filterStatus === s.key ? "bg-zinc-800" : "hover:bg-zinc-800/50"
            )}
          >
            <span className={s.color}>{s.count}</span>
            <span className="text-zinc-500 ml-1">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm bg-zinc-900 border-zinc-800"
            data-testid="search-input"
          />
        </div>
        {filterStatus !== "all" && (
          <Button variant="ghost" size="sm" onClick={() => setFilterStatus("all")} className="h-8 text-xs">
            Clear
          </Button>
        )}
      </div>

      {/* Tasks Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {/* Header */}
          <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 border-b border-zinc-800 text-[10px] text-zinc-500 uppercase tracking-wider">
            <div className="col-span-3">Task</div>
            <div className="col-span-2">Project</div>
            <div className="col-span-1">Type</div>
            <div className="col-span-1">Due</div>
            <div className="col-span-1 text-right">Price</div>
            <div className="col-span-1">Stage</div>
            <div className="col-span-3">Progress</div>
          </div>
          
          {filteredTasks.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">No tasks found</div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {filteredTasks.map(task => {
                const stage = task.resolved_stage;
                const needsAction = taskRequiresAction(task);
                
                return (
                  <Link
                    key={task.id}
                    to={`/tasks/${task.id}`}
                    className={cn(
                      "grid grid-cols-12 gap-2 px-3 py-2.5 hover:bg-zinc-800/30 transition-colors items-center text-sm",
                      needsAction && "bg-red-500/5 border-l-2 border-l-red-500"
                    )}
                    data-testid={`task-row-${task.id}`}
                  >
                    {/* Task Name */}
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      {needsAction && <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                      <span className={cn("truncate", needsAction ? "text-red-400" : "text-zinc-50")}>
                        {task.name}
                      </span>
                    </div>
                    
                    {/* Project */}
                    <div className="col-span-2 text-zinc-400 truncate text-xs hidden md:block">
                      {task.project_name}
                    </div>
                    
                    {/* Type */}
                    <div className="col-span-1 hidden md:block">
                      <span className="text-zinc-500 text-xs">{getTypeLabel(task.task_type)}</span>
                    </div>
                    
                    {/* Deadline */}
                    <div className="col-span-1 text-zinc-500 text-xs hidden md:block">
                      {formatDate(task.deadline)}
                    </div>
                    
                    {/* Price */}
                    <div className="col-span-1 text-right text-zinc-50 font-mono text-xs hidden md:block">
                      {formatPrice(task.client_price || task.agreed_price)}
                    </div>
                    
                    {/* Status */}
                    <div className="col-span-1 hidden md:block">
                      <TaskStageBadge task={task} size="sm" />
                    </div>
                    
                    {/* Timeline */}
                    <div className="col-span-3 hidden md:flex items-center gap-2">
                      {stage?.stage_count ? (
                        <div className="flex items-center gap-0.5 flex-1" title={`${stage.name} · ${stage.stage_index + 1}/${stage.stage_count}`}>
                          {Array.from({ length: stage.stage_count }).map((_, idx) => (
                            <React.Fragment key={idx}>
                              <div className={cn("w-2 h-2 rounded-full shrink-0", idx <= stage.stage_index ? "bg-green-500" : "bg-zinc-700")} />
                              {idx < stage.stage_count - 1 && (
                                <div className={cn("flex-1 h-0.5 min-w-[8px]", idx < stage.stage_index ? "bg-green-500" : "bg-zinc-700")} />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <span className="text-zinc-600 text-xs">—</span>
                      )}
                      <ChevronRight className="h-3.5 w-3.5 text-zinc-600 ml-auto shrink-0" />
                    </div>
                    
                    {/* Mobile */}
                    <div className="col-span-9 flex items-center justify-between md:hidden">
                      <div className="flex items-center gap-2">
                        <TaskStageBadge task={task} size="sm" />
                        <span className="text-zinc-50 font-mono text-xs">
                          {formatPrice(task.client_price || task.agreed_price)}
                        </span>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
